import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import type { Form, FormSchema, FormStatus } from '../types.js';
import { formsApi, getClient, getConfig, getMe } from '../api.js';
import SchemaEditor from '../components/SchemaEditor.js';
import CopyButton from '../components/CopyButton.js';

const WORKER_BASE = 'https://forms.conversionhive.au';

function buildAiPrompt(
  formId: string,
  clientName: string,
  schema: FormSchema,
  turnstileEnabled: boolean,
  allowedOrigins: string[],
  turnstileSiteKey: string,
  captchaProvider: 'none' | 'turnstile' | 'recaptcha',
  recaptchaSiteKey: string,
): string {
  const fieldLines = schema.fields
    .map((f) => `- ${f.name} (${f.type}${f.required ? ', required' : ''}): ${f.label}`)
    .join('\n');
  const origins = allowedOrigins.length > 0
    ? allowedOrigins.join(', ')
    : '(none configured yet — owner must add the website URL in the dashboard before cross-origin submissions will work)';
  const redirectUrl = schema.success_redirect_url
    ? `\n- **Success redirect:** After a successful POST, the server includes \`redirect_url\` in the JSON response pointing to ${schema.success_redirect_url} with all form fields appended as query params. Your submit handler MUST honor it (see below).`
    : '\n- **Success redirect:** Not configured. Show success_message on the same page.';

  const hasFiles = schema.fields.some((f) => f.type === 'file');
  const showTurnstile = hasFiles ? captchaProvider === 'turnstile' : turnstileEnabled;
  const ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';
  const inputLine = (f: FormSchema['fields'][number]): string => {
    if (f.type === 'file') {
      const max = f.max_files ?? 5;
      return `  <input name="${f.name}" type="file" accept="${ACCEPT}"${max > 1 ? ' multiple' : ''} data-max-files="${max}"${f.required ? ' required' : ''}>`;
    }
    return `  <input name="${f.name}" type="${f.type === 'textarea' ? 'text' : f.type}"${f.required ? ' required' : ''} placeholder="${f.label}">`;
  };
  const tokenLine = captchaProvider === 'recaptcha' && recaptchaSiteKey
    ? `const token = await grecaptcha.execute('${recaptchaSiteKey}', { action: 'submit' });`
    : captchaProvider === 'turnstile'
      ? `const token = form.querySelector('[name="cf-turnstile-response"]')?.value ?? '';`
      : `const token = '';`;
  const recaptchaScript = hasFiles && captchaProvider === 'recaptcha' && recaptchaSiteKey
    ? `\n<script src="https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}"></script>`
    : '';
  const successHtml = (schema.success_message ?? 'Thanks — we will be in touch.').replace(/'/g, "\\'");
  const fileLabels = JSON.stringify(Object.fromEntries(schema.fields.map((f) => [f.name, f.label]))).replace(/</g, '\\u003c');
  const submitScript = hasFiles
    ? `<script>
// This form takes files: send it as multipart and put the bot token in X-Captcha-Token.
// Do NOT set Content-Type yourself — the browser adds the multipart boundary.
document.getElementById('ch-form').addEventListener('submit', async (e) => {
  e.preventDefault();
  const form = e.target;
  const btn = form.querySelector('button[type=submit]');
  const btnText = btn.textContent;
  function resetCaptcha() {
    const widget = form.querySelector('.cf-turnstile');
    if (widget && window.turnstile) {
      try { window.turnstile.reset(widget); } catch {}
    }
  }
  const inputs = [...form.querySelectorAll('input[type=file]')];
  for (const input of inputs) {
    const max = Number(input.dataset.maxFiles) || 5;
    if (input.files.length > max) { alert('Attach no more than ' + max + ' files to this field.'); return; }
  }
  const files = inputs.flatMap((i) => [...i.files]);
  const types = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'application/pdf'];
  if (files.some((f) => !types.includes(f.type) && !/\\.(jpg|jpeg|png|webp|heic|heif|pdf)$/i.test(f.name))) {
    alert('Only JPG, PNG, WebP, HEIC or PDF files can be uploaded');
    return;
  }
  const total = files.reduce((n, f) => n + f.size, 0);
  if (files.length > 5 || files.some((f) => f.size > 10 * 1024 * 1024) || total > 20 * 1024 * 1024) {
    alert('Please attach up to 5 files: 10 MB each and 20 MB in total.');
    return;
  }
  btn.disabled = true;
  try {
    ${tokenLine}
    btn.textContent = 'Uploading…';
    const res = await fetch('${WORKER_BASE}/submit/${formId}', {
      method: 'POST',
      headers: { 'X-Captcha-Token': token },
      body: new FormData(form),
    });
    const json = await res.json();
    if (json.ok) {
      if (json.redirect_url) { window.location.href = json.redirect_url; return; }
      form.outerHTML = '<p>${successHtml}</p>';
    }
    else {
      const labels = ${fileLabels};
      const field = Object.keys(json.field_errors ?? {})[0];
      alert(field ? (labels[field] || field) + ': ' + json.field_errors[field] : json.error || 'Submission failed');
      btn.disabled = false;
      btn.textContent = btnText;
      resetCaptcha();
    }
  } catch { alert('Network error'); btn.disabled = false; btn.textContent = btnText; resetCaptcha(); }
});
</script>`
    : `<script>
document.getElementById('ch-form').addEventListener('submit', async (e) => {
  e.preventDefault();
  const btn = e.target.querySelector('button[type=submit]');
  btn.disabled = true;
  try {
    const data = Object.fromEntries(new FormData(e.target));
    const res = await fetch('${WORKER_BASE}/submit/${formId}', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (json.ok) {
      if (json.redirect_url) { window.location.href = json.redirect_url; return; }
      e.target.outerHTML = '<p>${successHtml}</p>';
    }
    else { alert(json.error || 'Submission failed'); btn.disabled = false; }
  } catch { alert('Network error'); btn.disabled = false; }
});
</script>`;

  return `You are embedding a contact form into a website using the Form Handler Platform.

## This form's details (pre-filled — do not change)

- **Form ID:** ${formId}
- **Client name:** ${clientName}
- **Submit endpoint:** POST ${WORKER_BASE}/submit/${formId}
- **Hosted form URL:** ${WORKER_BASE}/f/${formId}
- **Embed script URL:** ${WORKER_BASE}/embed.js
- **Schema endpoint:** GET ${WORKER_BASE}/api/public/schema/${formId}
- **Turnstile bot protection:** ${showTurnstile ? 'ENABLED — Turnstile site key will be included in the schema response' : 'DISABLED'}
- **Allowed origins (CORS):** ${origins}${redirectUrl}

## Form fields (must match exactly — snake_case names below are the input \`name\` attributes)

${fieldLines}

## Rules you must follow

1. Use the Form ID \`${formId}\` verbatim. Do not invent a new one.
2. If the user's website is not in the allowed origins list above, tell them they must add it in the dashboard BEFORE the form will work — otherwise submissions will get 403 Forbidden.
3. Field \`name\` attributes MUST match the snake_case names in the schema exactly. Don't rename or camelCase them.
4. Always include the honeypot field: \`<input type="hidden" name="_hp_website" style="display:none" tabindex="-1" autocomplete="off">\` — never remove or style it visible.
5. ${hasFiles ? 'This form takes FILES: POST it as `multipart/form-data` (`new FormData(form)`, no Content-Type header) with the bot token in the `X-Captcha-Token` header - see Method C.' : 'POST body is `application/json` OR `application/x-www-form-urlencoded`. No multipart (no file uploads).'}
6. ${hasFiles ? 'Files: JPG, PNG, WebP, HEIC or PDF; 10 MB each; at most 5 files and 20 MB per submission. People receive them as download links.' : 'Max payload: 100 KB.'}
7. Lead tracking (IMPORTANT): add this script tag to the page so the lead's source (UTM) is captured and the conversion event fires — <script src="${WORKER_BASE}/t.js" async></script>. Method B (embed script) loads it automatically; for Method C (custom HTML) you MUST include it (shown below). Method A (iframe) cannot read the parent page's UTM, so prefer Method B or C when source tracking matters.

## Pick the simplest embed method that fits the user's site

**Method A — Iframe (default for most sites):**
\`\`\`html
<iframe src="${WORKER_BASE}/f/${formId}"
  width="100%" height="600" style="border:0" title="${clientName}"></iframe>
\`\`\`
Works with no CORS setup. Good for WordPress, Wix, Squarespace, etc.

**Method B — Embed script (inline with site styling):**
\`\`\`html
<div data-ch-form="${formId}"></div>
<script src="${WORKER_BASE}/embed.js" async></script>
\`\`\`
Requires the site's origin to be in the allowed origins list.

**Method C — Custom HTML + direct API (full styling control):**
\`\`\`html
<form id="ch-form">
  <!-- Fields matching the schema above -->
${schema.fields.map(inputLine).join('\n')}
  <input type="hidden" name="_hp_website" style="display:none" tabindex="-1" autocomplete="off">${showTurnstile && turnstileSiteKey ? `\n  <!-- Turnstile bot check — REQUIRED because turnstile_enabled is true -->\n  <div class="cf-turnstile" data-sitekey="${turnstileSiteKey}"></div>` : ''}
  <button type="submit">${schema.submit_button_text ?? 'Submit'}</button>
</form>${recaptchaScript}${showTurnstile && turnstileSiteKey ? `\n<script src="https://challenges.cloudflare.com/turnstile/v0/api.js" async defer></script>` : ''}
${submitScript}
<!-- Lead-source tracking + conversion event — REQUIRED for custom HTML -->
<script src="${WORKER_BASE}/t.js" async></script>
\`\`\`
Requires the site's origin to be in the allowed origins list.

## First ask the user

1. What platform is your website on? (WordPress / Shopify / static HTML / Next.js / other)
2. Do you want the form to match your site's styling, or is the default OK?
3. Have you added your website URL to the allowed origins in the dashboard?

Then output the exact code to paste. Keep it minimal — no unnecessary frameworks or wrappers.`;
}

export default function FormEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [clientName, setClientName] = useState('');

  const [name, setName] = useState('');
  const [status, setStatus] = useState<FormStatus>('active');
  const [isPrimary, setIsPrimary] = useState(false);
  const [schema, setSchema] = useState<FormSchema>({ fields: [] });
  const [notifyEmails, setNotifyEmails] = useState('');
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [turnstileEnabled, setTurnstileEnabled] = useState(false);
  const [captchaProvider, setCaptchaProvider] = useState<'none' | 'turnstile' | 'recaptcha'>('recaptcha');
  const [enhancedConversions, setEnhancedConversions] = useState(false);
  const [clientId, setClientId] = useState('');
  const [allowedOrigins, setAllowedOrigins] = useState<string[]>([]);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState('');
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState('');

  // Test email — recipient is always the signed-in user, never an input field.
  const [myEmail, setMyEmail] = useState<string | null>(null);
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  const [testModeUntil, setTestModeUntil] = useState<number | null>(null);
  const [nowTick, setNowTick] = useState(Math.floor(Date.now() / 1000));
  const [testModeBusy, setTestModeBusy] = useState(false);
  // serverNow − browserNow. The worker enforces the deadline against its own clock,
  // so a skewed local clock would otherwise make the banner disappear while the
  // form is still muted (or linger after delivery has resumed).
  const [clockOffset, setClockOffset] = useState(0);

  useEffect(() => {
    if (!id) return;
    formsApi.get(id)
      .then((res) => {
        const f: Form = res.form;
        setName(f.name);
        setStatus(f.status);
        setIsPrimary(f.is_primary);
        setSchema(f.form_schema);
        setNotifyEmails(f.notify_emails.join('\n'));
        setEmailEnabled(f.email_enabled);
        setWebhookUrl(f.webhook_url ?? '');
        setWebhookSecret(f.webhook_secret ?? '');
        setTurnstileEnabled(f.turnstile_enabled);
        setCaptchaProvider(f.captcha_provider ?? 'recaptcha');
        setEnhancedConversions(f.enhanced_conversions_enabled ?? false);
        setTestModeUntil(f.test_mode_until ?? null);
        if (res.now) setClockOffset(res.now - Math.floor(Date.now() / 1000));
        setClientId(f.client_id);
        return getClient(f.client_id);
      })
      .then((res) => {
        setClientName(res.client.name);
        setAllowedOrigins(res.client.allowed_origins);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
    getConfig()
      .then((c) => { setTurnstileSiteKey(c.turnstile_site_key); setRecaptchaSiteKey(c.recaptcha_site_key ?? ''); })
      .catch(() => { /* optional */ });
    getMe().then(setMyEmail).catch(() => { /* shown by the server response instead */ });
  }, [id]);

  // Drive the countdown locally — no polling. Stops itself once the deadline passes.
  useEffect(() => {
    if (!testModeUntil) return;
    const serverNow = () => Math.floor(Date.now() / 1000) + clockOffset;
    if (serverNow() >= testModeUntil) return;
    const t = setInterval(() => {
      setNowTick(Math.floor(Date.now() / 1000));
      if (serverNow() >= testModeUntil) clearInterval(t);
    }, 1000);
    return () => clearInterval(t);
  }, [testModeUntil, clockOffset]);

  const testSecondsLeft = testModeUntil ? Math.max(0, testModeUntil - (nowTick + clockOffset)) : 0;
  const testModeOn = testSecondsLeft > 0;

  async function handleEnableTestMode() {
    if (!id) return;
    setTestModeBusy(true);
    try {
      const res = await formsApi.enableTestMode(id);
      setNowTick(Math.floor(Date.now() / 1000));
      if (res.now) setClockOffset(res.now - Math.floor(Date.now() / 1000));
      setTestModeUntil(res.test_mode_until);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not enable test mode');
    } finally {
      setTestModeBusy(false);
    }
  }

  async function handleDisableTestMode() {
    if (!id) return;
    setTestModeBusy(true);
    try {
      await formsApi.disableTestMode(id);
      setTestModeUntil(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not turn test mode off');
    } finally {
      setTestModeBusy(false);
    }
  }

  async function handleTestEmail() {
    if (!id) return;
    setTestSending(true);
    setTestResult(null);
    try {
      const res = await formsApi.sendTestEmail(id);
      setTestResult({ ok: true, message: `Sent to ${res.recipient} from ${res.sender}.` });
    } catch (e) {
      setTestResult({ ok: false, message: e instanceof Error ? e.message : 'Test send failed' });
    } finally {
      setTestSending(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    setSaving(true);
    setError('');
    setSuccessMsg('');

    const patch: Parameters<typeof formsApi.update>[1] = {
      name: name.trim(),
      status,
      is_primary: isPrimary,
      form_schema: schema,
      notify_emails: notifyEmails.split('\n').map((s) => s.trim()).filter(Boolean),
      email_enabled: emailEnabled,
      ...(webhookUrl.trim() ? { webhook_url: webhookUrl.trim() } : {}),
      ...(webhookSecret.trim() ? { webhook_secret: webhookSecret.trim() } : {}),
      turnstile_enabled: captchaProvider === 'turnstile',
      captcha_provider: captchaProvider,
      enhanced_conversions_enabled: enhancedConversions,
    };

    try {
      await formsApi.update(id, patch);
      setSuccessMsg('Changes saved.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p style={{ color: '#94a3b8' }}>Loading…</p>;

  return (
    <>
      <div className="page-header">
        <div>
          {clientId && (
            <div style={{ fontSize: '.85rem', color: '#64748b', marginBottom: '.25rem' }}>
              <Link to={`/clients/${clientId}`} style={{ color: '#2563eb' }}>
                ← {clientName || 'Back to client'}
              </Link>
            </div>
          )}
          <h1 className="page-title">Edit form: {name}</h1>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {successMsg && <div className="alert alert-success">{successMsg}</div>}

      <form onSubmit={(e) => { void handleSubmit(e); }}>
        {/* ── Basic ── */}
        <div className="card">
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Basic</h2>

          <div className="form-group">
            <label>Form name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value as FormStatus)}>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={isPrimary}
                onChange={(e) => setIsPrimary(e.target.checked)}
                style={{ width: 'auto', marginRight: '.4rem' }}
              />
              Use as primary form for legacy embed URLs
            </label>
          </div>
        </div>

        {/* ── Schema ── */}
        <div className="card">
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Schema</h2>
          <SchemaEditor schema={schema} onChange={setSchema} />
        </div>

        {/* ── Notifications ── */}
        <div className="card">
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Notifications</h2>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={emailEnabled}
                onChange={(e) => setEmailEnabled(e.target.checked)}
                style={{ width: 'auto', marginRight: '.4rem' }}
              />
              Enable email notifications
            </label>
          </div>

          {emailEnabled && (
            <div className="form-group">
              <label>Notify emails (one per line)</label>
              <textarea
                value={notifyEmails}
                onChange={(e) => setNotifyEmails(e.target.value)}
                style={{ minHeight: '80px' }}
                placeholder="admin@example.com"
              />
            </div>
          )}
        </div>

        {/* ── Test email ──
            No input element here on purpose: the worker addresses the test to
            the signed-in user's own email, so a client can never be emailed. */}
        <div className="card">
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '.5rem' }}>Test email</h2>
          <p style={{ color: '#475569', fontSize: '.9rem', marginBottom: '.75rem' }}>
            Sends one email through this client's real delivery pipeline — same SMTP2GO key,
            same From address, same templates — using sample data from this form's own fields.
            It goes only to <strong>{myEmail ?? 'your dashboard login address'}</strong>.
            The client is never emailed, and no submission is recorded.
          </p>

          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => { void handleTestEmail(); }}
            disabled={testSending || !id}
          >
            {testSending ? 'Sending…' : 'Send test email to me'}
          </button>

          {testResult && (
            <div
              className={`alert ${testResult.ok ? 'alert-success' : 'alert-error'}`}
              style={{ marginTop: '.75rem', marginBottom: 0 }}
            >
              {testResult.ok ? '✓ ' : 'Test email failed: '}{testResult.message}
            </div>
          )}

          <div className="form-hint" style={{ marginTop: '.5rem' }}>
            Uses one send from the shared SMTP2GO monthly quota. Save any unsaved changes first —
            the test uses the form as currently stored.
          </div>
        </div>

        {/* ── Webhook ── */}
        <div className="card">
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Webhook</h2>

          <div className="form-group">
            <label>Webhook URL (HTTPS only)</label>
            <input
              type="url"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://example.com/webhook"
            />
          </div>

          <div className="form-group">
            <label>Webhook secret (for HMAC signature)</label>
            <input
              type="text"
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
              placeholder="Leave blank to generate none"
            />
          </div>
        </div>

        {/* ── Security ── */}
        <div className="card">
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Security</h2>

          <div className="form-group">
            <label>Bot protection</label>
            <select
              value={captchaProvider}
              onChange={(e) => setCaptchaProvider(e.target.value as 'none' | 'turnstile' | 'recaptcha')}
            >
              <option value="recaptcha">reCAPTCHA (recommended)</option>
              <option value="turnstile">Turnstile</option>
              <option value="none">None</option>
            </select>
            <div className="form-hint">reCAPTCHA supports unlimited client domains. Turnstile is privacy-friendly but capped at 10 domains total.</div>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={enhancedConversions}
                onChange={(e) => setEnhancedConversions(e.target.checked)}
                style={{ width: 'auto', marginRight: '.4rem' }}
              />
              Enhanced Conversions — send submitter email/phone to Google/Meta
            </label>
            <div className="form-hint">
              Off by default. The lead event always fires so you can count conversions; turning this on also
              attaches the (hashed-by-GTM) email/phone for ad matching. Only enable once the client's privacy
              policy covers it and a GTM conversion tag is set up.
            </div>
          </div>

          {/* Legacy turnstile_enabled checkbox removed — derived from captcha_provider on save */}
        </div>

        {/* ── Test mode ── */}
        <div className="card">
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '.75rem' }}>Test mode</h2>
          {testModeOn ? (
            <>
              <div className="alert alert-success" style={{ marginBottom: '.75rem' }}>
                🧪 Test mode active — {Math.floor(testSecondsLeft / 60)}:{String(testSecondsLeft % 60).padStart(2, '0')} remaining.
                Submissions in this window are flagged as tests and will <strong>not</strong> email the client or hit their webhook.
              </div>
              <button type="button" className="btn btn-secondary" disabled={testModeBusy}
                onClick={() => { void handleDisableTestMode(); }}>
                Turn off now
              </button>
            </>
          ) : (
            <>
              <p style={{ color: '#475569', fontSize: '.9rem', marginBottom: '.75rem' }}>
                Submit this client's real form to check it works. For 5 minutes, submissions are saved
                and flagged as tests — no email to the client, no webhook. It switches itself off.
              </p>
              <button type="button" className="btn btn-secondary" disabled={testModeBusy}
                onClick={() => { void handleEnableTestMode(); }}>
                {testModeBusy ? 'Enabling…' : 'Enable test mode (5 min)'}
              </button>
            </>
          )}
        </div>

        <div style={{ display: 'flex', gap: '.75rem', marginTop: '1rem' }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate(`/clients/${clientId}`)}>
            Cancel
          </button>
        </div>
      </form>

      {/* ── Embed code ── */}
      {id && (
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '.5rem' }}>Embed code</h2>
          <p style={{ color: '#475569', fontSize: '.9rem', marginBottom: '.75rem' }}>
            Paste this into the client's website where the form should appear.
          </p>
          <pre style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '.75rem', fontSize: '.8rem', overflowX: 'auto', marginBottom: '.5rem' }}>
            {`<div data-ch-form="${id}"></div>\n<script src="${WORKER_BASE}/embed.js" async></script>`}
          </pre>
          <CopyButton text={`<div data-ch-form="${id}"></div>\n<script src="${WORKER_BASE}/embed.js" async></script>`} label="Copy embed code" />

          <h3 style={{ fontSize: '.9rem', fontWeight: 600, margin: '1.25rem 0 .5rem' }}>Public URLs</h3>
          <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '.875rem', lineHeight: '2' }}>
            <li>Hosted form: <a href={`${WORKER_BASE}/f/${id}`} target="_blank" rel="noreferrer" style={{ fontFamily: 'monospace', fontSize: '.85rem' }}>{`${WORKER_BASE}/f/${id}`}</a></li>
            <li>Submit endpoint: <code style={{ fontSize: '.85rem' }}>{`${WORKER_BASE}/submit/${id}`}</code></li>
            <li>Schema (used by embed.js): <code style={{ fontSize: '.85rem' }}>{`${WORKER_BASE}/api/public/schema/${id}`}</code></li>
          </ul>
        </div>
      )}

      {/* ── AI assistant prompt ── */}
      {id && (
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '.5rem' }}>AI assistant prompt</h2>
          <p style={{ color: '#475569', fontSize: '.9rem', marginBottom: '.75rem' }}>
            Copy this and paste to ChatGPT / Claude — it will embed this form for you. The Form ID and field info are already filled in.
          </p>
          <CopyButton text={buildAiPrompt(id, clientName, schema, turnstileEnabled, allowedOrigins, turnstileSiteKey, captchaProvider, recaptchaSiteKey)} label="Copy AI prompt" />
          <pre style={{
            background: '#0f172a',
            color: '#e2e8f0',
            padding: '1rem',
            borderRadius: '6px',
            fontSize: '.75rem',
            lineHeight: '1.5',
            overflow: 'auto',
            maxHeight: '400px',
            marginTop: '.75rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}>
            {buildAiPrompt(id, clientName, schema, turnstileEnabled, allowedOrigins, turnstileSiteKey, captchaProvider, recaptchaSiteKey)}
          </pre>
        </div>
      )}

    </>
  );
}
